// Batch PDF export. Legacy: batchExportPrinterPDF/batchExportSheetPDF (L8347-8414).
// Phase 3 ships the printer path (one label per page, sized to the label). The sheet
// (Avery N-up) path lands with the size picker in Phase 5 once SHEET_LAYOUTS is wired.
import { jsPDF } from 'jspdf'
import { labelMm } from './../canvas'
import { state } from './../state'
import { batchRenderAllLabels } from './engine'
import { clipImagesToShape } from './../shape-clip'
import { sheetCapacity } from './../constants'
import type { SheetLayout } from './../types'

/** Build a multi-page PDF, one rendered label per page (printer layout). */
export function buildPrinterPDF(images: string[]): jsPDF {
  const { w: mmW, h: mmH } = labelMm()
  const orientation = mmW > mmH ? 'landscape' : 'portrait'
  const pdf = new jsPDF({ orientation, unit: 'mm', format: [mmW, mmH] })
  images.forEach((img, i) => {
    if (i > 0) pdf.addPage([mmW, mmH], orientation)
    if (img) pdf.addImage(img, 'PNG', 0, 0, mmW, mmH, undefined, 'FAST')
  })
  return pdf
}

/** Build an N-up sheet PDF (Avery-style), tiling labels into the sheet grid
 *  (legacy batchExportSheetPDF). `startAt` skips already-used cells (0-based). */
export function buildSheetPDF(images: string[], sheet: SheetLayout, startAt = 0): jsPDF {
  const isA4 = Math.round(sheet.pageWidthMM) === 210
  const perSheet = sheetCapacity(sheet) // NOT columns*rows: some die-cut grids are partial
  const totalPages = Math.max(1, Math.ceil((images.length + startAt) / perSheet))
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: isA4 ? 'a4' : 'letter' })

  for (let page = 0; page < totalPages; page++) {
    if (page > 0) pdf.addPage()
    for (let row = 0; row < sheet.rows; row++) {
      for (let col = 0; col < sheet.columns; col++) {
        const cell = row * sheet.columns + col
        if (cell >= perSheet) continue // beyond the real die-cut count on this sheet
        const dataIdx = page * perSheet + cell - startAt
        if (dataIdx < 0 || dataIdx >= images.length || !images[dataIdx]) continue
        const x = sheet.marginLeftMM + col * (sheet.labelWidthMM + sheet.gapXMM)
        const y = sheet.marginTopMM + row * (sheet.labelHeightMM + sheet.gapYMM)
        pdf.addImage(images[dataIdx], 'PNG', x, y, sheet.labelWidthMM, sheet.labelHeightMM, undefined, 'FAST')
      }
    }
  }
  return pdf
}

/** Render all rows and return the PDF as bytes (used by tests / programmatic callers).
 *  Uses the sheet layout if one is selected, else the printer (one label/page) layout. */
export async function batchPdfBytes(repeat = 1, startAt = 0): Promise<{ bytes: ArrayBuffer; pages: number }> {
  const images = await batchRenderAllLabels(repeat)
  const sheet = state.currentSheet
  const pdf = sheet ? buildSheetPDF(await clipImagesToShape(images, sheet.shape), sheet, startAt) : buildPrinterPDF(images)
  return { bytes: pdf.output('arraybuffer'), pages: pdf.getNumberOfPages() }
}

/** Render all rows and trigger a browser download (legacy batchExportPDF). */
export async function batchExportPDF(filename = 'batch.pdf', repeat = 1, startAt = 0): Promise<number> {
  const images = await batchRenderAllLabels(repeat)
  const sheet = state.currentSheet
  const pdf = sheet ? buildSheetPDF(await clipImagesToShape(images, sheet.shape), sheet, startAt) : buildPrinterPDF(images)
  pdf.save(filename)
  return images.length
}
